/**
 * Copy text that is only known after an asynchronous step (e.g. a voucher the server signs first).
 *
 * `navigator.clipboard.writeText` after an `await` fails in Safari (Mac, iPhone, iPad): the click's user
 * activation is used up by then. Handing the clipboard a `ClipboardItem` whose content is still a promise
 * keeps the write inside the click — Safari and Chromium accept it. Where that is not available, the text
 * is awaited and written the plain way.
 *
 * Resolves to the text when it was copied, and rejects with the text attached (`error.text`) when the
 * browser refused — so the caller can still show it for copying by hand.
 */
export class ClipboardRefusedError extends Error {
  constructor(public text: string, cause?: unknown) {
    super("The browser refused to write to the clipboard");
    (this as any).cause = cause;
  }
}

export const copyTextWhenReady = async (text: Promise<string>): Promise<string> => {
  const ClipboardItemCtor = (globalThis as any).ClipboardItem;
  if (ClipboardItemCtor && navigator.clipboard?.write) {
    // Started synchronously in the click; the content follows when the promise settles.
    const blob = text.then((value) => new Blob([value], { type: "text/plain" }));
    const written = navigator.clipboard.write([new ClipboardItemCtor({ "text/plain": blob })]);
    // A failed fetch of the text also fails the write — that error is the one the caller gets, not this.
    written.catch(() => undefined);
    const value = await text;
    try {
      await written;
      return value;
    } catch (error) {
      throw new ClipboardRefusedError(value, error);
    }
  }
  const value = await text;
  try {
    await navigator.clipboard.writeText(value);
    return value;
  } catch (error) {
    throw new ClipboardRefusedError(value, error);
  }
};
